import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { useRouteLoaderData } from '@remix-run/react';

import { LoadingSpinner } from '~/components/LoadingSpinner';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import remixI18n from '~/i18next.server';
import type { Community, CurrentCommunityMember } from '~/interfaces/community';
import type { Subscription } from '~/interfaces/subscriptions';
import type { RootLoaderData } from '~/root';
import { getCommunityLoaderData } from '~/services/api/community/getCommunityLoaderData.server';
import getSubscriptionProfile from '~/services/api/subscription/getSubsciptionProfile.server';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import { publicationFromMatches } from '~/utils/publicationFromLoaderData';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';

import Page from './_components/v2/Page';
import ReadOnlyPage from './_components/v2/ReadOnlyPage';
import { getSiteFontsData } from './_utils/withSiteFonts';

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const publication = publicationFromMatches(matches);
  const metaTitle = data?.metaTitle;

  if (!publication || !metaTitle) {
    return [{ robots: 'noindex' }];
  }

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    returnEarlyCheck: !metaTitle,
    overrides: {
      robots: 'noindex',
      title: metaTitle,
      description: `Make the most of your ${publication.name} subscription by managing your personal settings.`,
    },
  });
};

export const loader: LoaderFunction = async ({ request, params, context }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);

  const sitePromise = context.getSite(request, session);
  const communityDataPromise = getCommunityLoaderData(request, session, sitePromise);
  const [site, subscription, [community, currentCommunityMember]] = await Promise.all([
    sitePromise,
    getSubscriptionProfile(request, session, params?.subscriberId),
    communityDataPromise,
  ]);
  const publication = site.publication;

  const locale = publication?.language || 'en';
  const [t, { fontsCss: renderableFontsCss, adobeFontUrls }] = await Promise.all([
    remixI18n.getFixedT(locale, 'common'),
    getSiteFontsData(site),
  ]);

  if (!subscription) {
    throw new Response(t('forms.toast.no_subscription'), { status: 404 });
  }

  return json({
    requestUrl: url.href,
    subscription,
    community,
    currentCommunityMember,
    subscriberId: params?.subscriberId,
    stripeSessionUrl: null,
    metaTitle: t('meta.titles.manage'),
    renderableFontsCss,
    adobeFontUrls,
  });
};

export interface ManageLoaderData {
  subscription: Subscription;
  community: Community | null;
  currentCommunityMember: CurrentCommunityMember | null;
  subscriberId: string;
  isLoggedIn: boolean;
}

const Manage = () => {
  const loaderData = useTypedLoaderData<ManageLoaderData>();
  const {
    site,
    isLoggedIn,
    isFetchingSubscription,
    subscription: loggedInUserSubscription,
  } = useRouteLoaderData('root') as RootLoaderData;
  const publication = site?.publication;

  if (!site || !publication || !loaderData.subscription) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }

  if (isFetchingSubscription) {
    return (
      <div className="flex justify-center items-center min-h-dvh">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const isReadOnly = isLoggedIn && loaderData.subscriberId !== loggedInUserSubscription?.subscriber_id;
  if (isReadOnly) {
    return <ReadOnlyPage site={site} loggedInSubscriberId={loggedInUserSubscription?.subscriber_id || ''} />;
  }

  return (
    <Page
      site={site}
      publication={publication}
      subscriberId={loaderData.subscriberId}
      subscription={loaderData.subscription}
      isLoggedIn={isLoggedIn}
      community={loaderData.community}
      currentCommunityMember={loaderData.currentCommunityMember}
    />
  );
};

export const shouldRevalidate = shouldRevalidateLoaderData;

export default Manage;
